import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { breakpoints, Tooltip, TooltipModel } from '@notino/react-styleguide';

import { useTrackingContext } from '@context/tracking/TrackingContext';
import { useIsDesktop } from '@utils/helpers';

import { getPictogramIcon } from './constants';
import {
  pictogramMoreInfoPathname,
  pictogramMoreInfoLinkText,
  pictogramTitles,
  pictogramTooltip,
} from './messages';
import { EPictograms } from './model';
import {
  MoreInfoLink,
  PictogramImage,
  PictogramItemWrapper,
  TooltipContent,
} from './styled';
import {
  trackOnClickMoreInfoTooltip,
  trackPictograms,
} from './trackPictograms';

interface IPictogramProps {
  pictogram: EPictograms;
  index: number;
}

export const Pictogram = ({ pictogram, index }: IPictogramProps) => {
  const { formatMessage } = useIntl();
  const isDesktop = useIsDesktop();

  const moreInfoPathname = formatMessage(pictogramMoreInfoPathname);

  const { getTimeFromInit } = useTrackingContext();

  if (!pictogramTooltip[pictogram] || !pictogramTitles[pictogram]) {
    return null;
  }

  const onShowTooltip = () => {
    trackPictograms(getTimeFromInit(), pictogram, isDesktop);
  };

  const onLinkClick = () => {
    trackOnClickMoreInfoTooltip(getTimeFromInit(), pictogram);
  };
  // TODO: ugly hack to display tooltip on mobile, fix after fixing tooltip behavior
  const position =
    !isDesktop && index % 2 !== 0
      ? TooltipModel.Position.topCenter
      : TooltipModel.Position.topAutomatic;

  return (
    <PictogramItemWrapper data-testid="pictogram-item">
      <Tooltip
        interactive={true}
        data-testid="pictogram-tooltip"
        content={
          <TooltipContent isDesktop={isDesktop}>
            <FormattedMessage {...pictogramTooltip[pictogram]} />
            {moreInfoPathname !== 'null' && (
              <MoreInfoLink
                href={moreInfoPathname}
                rel="nofollow"
                target="__blank"
                onClick={onLinkClick}
              >
                {formatMessage(pictogramMoreInfoLinkText)}
              </MoreInfoLink>
            )}
          </TooltipContent>
        }
        position={position}
        recalculateOnResize={true}
        alignDesktop={true}
        wrapText={!isDesktop}
        breakpointToCenter={breakpoints.xl}
        onShow={onShowTooltip}
      >
        <PictogramImage
          src={getPictogramIcon(pictogram)}
          data-testid="pictogram-icon"
          alt={formatMessage(pictogramTooltip[pictogram])}
        />
      </Tooltip>
      <FormattedMessage {...pictogramTitles[pictogram]} />
    </PictogramItemWrapper>
  );
};
